import { useCallback, useEffect, useMemo, useState } from 'react';
import { AutomationRuleDto, automationRuleService } from '../services/automationRuleService';
import { AutomationRule, RuleFilterGroup, RuleUpdateGroup } from '../types';

export interface AutomationRulesState {
  rules: AutomationRule[];
  isLoading: boolean;
  searchTerm: string;
  filterEntity: string;
  filterStatus: string;
  filterTrigger: string;
  showRelatedPlaybooks: boolean;
}

export const useAutomationRules = () => {
  const [state, setState] = useState<AutomationRulesState>({
    rules: [],
    isLoading: false,
    searchTerm: '',
    filterEntity: '',
    filterStatus: '',
    filterTrigger: '',
    showRelatedPlaybooks: true,
  });

  const mapDtoToRule = useCallback((dto: AutomationRuleDto): AutomationRule => {
    const filterGroups: RuleFilterGroup[] = dto.filter?.filterGroups || [];
    const updateGroups: RuleUpdateGroup[] = dto.filter?.updateGroups || [];

    return {
      id: dto.ruleId,
      name: dto.name,
      description: dto.description,
      status: dto.active ? 'Active' : 'Inactive',
      lastModified: dto.updatedAt || dto.createdAt || new Date().toISOString(),
      entityType: dto.triggerEntity,
      triggerType: dto.triggerEvent,
      playbookIds: dto.playbooks?.map(p => p.playbookId) || [],
      filterGroups,
      updateGroups,
    };
  }, []);

  const fetchRules = useCallback(async () => {
    if (state.rules.length > 0 || state.isLoading) return;

    setState(prev => ({ ...prev, isLoading: true }));
    try {
      const data = await automationRuleService.getAllAutomationRules();
      setState(prev => ({
        ...prev,
        rules: Array.isArray(data) ? data.map(mapDtoToRule) : [],
        isLoading: false,
      }));
    } catch (error) {
      console.error('Failed to fetch automation rules', error);
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [mapDtoToRule]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const setSearchTerm = useCallback((term: string) => {
    setState(prev => ({ ...prev, searchTerm: term }));
  }, []);

  const setFilterEntity = useCallback((entity: string) => {
    setState(prev => ({ ...prev, filterEntity: entity }));
  }, []);

  const setFilterStatus = useCallback((status: string) => {
    setState(prev => ({ ...prev, filterStatus: status }));
  }, []);

  const setFilterTrigger = useCallback((trigger: string) => {
    setState(prev => ({ ...prev, filterTrigger: trigger }));
  }, []);

  const toggleShowRelatedPlaybooks = useCallback(() => {
    setState(prev => ({ ...prev, showRelatedPlaybooks: !prev.showRelatedPlaybooks }));
  }, []);

  const updateRuleInList = useCallback((ruleId: string, updates: Partial<AutomationRule>) => {
    setState(prev => ({
      ...prev,
      rules: prev.rules.map(r => (r.id === ruleId ? { ...r, ...updates, lastModified: new Date().toISOString() } : r)),
    }));
  }, []);

  const unlinkPlaybook = useCallback(async (ruleId: string, playbookId: string) => {
    try {
      await automationRuleService.removePlaybookFromRule(ruleId, playbookId);
      setState(prev => ({
        ...prev,
        rules: prev.rules.map(r => (r.id === ruleId ? { ...r, playbookIds: r.playbookIds.filter(id => id !== playbookId) } : r)),
      }));
    } catch (error) {
      console.error(`Failed to unlink playbook ${playbookId} from rule ${ruleId}`, error);
    }
  }, []);

  const deleteRule = useCallback(async (ruleId: string) => {
    try {
      await automationRuleService.deleteAutomationRule(ruleId);
      setState(prev => ({
        ...prev,
        rules: prev.rules.filter(r => r.id !== ruleId),
      }));
    } catch (error) {
      console.error(`Failed to delete rule ${ruleId}`, error);
    }
  }, []);

  const addRule = useCallback((rule: AutomationRule) => {
    setState(prev => ({
      ...prev,
      rules: [rule, ...prev.rules],
    }));
  }, []);

  const filteredRules = useMemo(() => {
    const { rules, searchTerm, filterEntity, filterStatus, filterTrigger } = state;
    const lowerCaseSearchTerm = searchTerm.toLowerCase();

    return rules
      .filter(
        rule =>
          !searchTerm ||
          rule.name.toLowerCase().includes(lowerCaseSearchTerm) ||
          rule.description.toLowerCase().includes(lowerCaseSearchTerm)
      )
      .filter(rule => !filterEntity || rule.entityType === filterEntity)
      .filter(rule => !filterStatus || rule.status === filterStatus)
      .filter(rule => !filterTrigger || rule.triggerType === filterTrigger);
  }, [state.rules, state.searchTerm, state.filterEntity, state.filterStatus, state.filterTrigger]);

  const availableEntities = useMemo(() => Array.from(new Set(state.rules.map(rule => rule.entityType))), [state.rules]);

  const availableTriggers = useMemo(() => Array.from(new Set(state.rules.map(rule => rule.triggerType))), [state.rules]);

  const availableStatuses = useMemo(() => Array.from(new Set(state.rules.map(rule => rule.status))), [state.rules]);

  return {
    ...state,
    filteredRules,
    availableEntities,
    availableTriggers,
    availableStatuses,
    setSearchTerm,
    setFilterEntity,
    setFilterStatus,
    setFilterTrigger,
    toggleShowRelatedPlaybooks,
    updateRuleInList,
    unlinkPlaybook,
    deleteRule,
    addRule,
    fetchRules,
  };
};
